import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { redirectToMainDomain, getLoginUrl } from '@/utils/domainUtils';
import { auth } from '@/firebase/config';
import { reload } from 'firebase/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Routes that don't require email verification
const PUBLIC_ROUTES = ['/login', '/accept-invitation'];
const VERIFICATION_ROUTE = '/verify-email';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Sync email verification status from Firebase Auth and Firestore
  useEffect(() => {
    const syncEmailVerification = async () => {
      if (user && !user.verified_email) {
        try {
          // Check Firestore first for invited users (they have verified_email: true in Firestore)
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userDoc = await getDoc(doc(db, 'users', user.id));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // If Firestore has verified_email flag (for invited users), use it
            if (userData.verified_email === true) {
              console.log('✅ User verified via invitation (from Firestore)');
              const updatedUser = { ...user, verified_email: true };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              window.location.reload();
              return;
            }
          }
          
          // For non-invited users, check Firebase Auth emailVerified status
          if (auth.currentUser) {
            // Reload user to get latest emailVerified status
            await reload(auth.currentUser);
            const isVerified = auth.currentUser.emailVerified;
            
            // If verified now but wasn't before, update the user in context
            if (isVerified && !user.verified_email) {
              // Update user in localStorage
              const updatedUser = { ...user, verified_email: true };
              localStorage.setItem('user', JSON.stringify(updatedUser));
              // Trigger a reload of the page to update context
              window.location.reload();
            }
          }
        } catch (error) {
          console.error('Error syncing email verification:', error);
        }
      }
    };

    if (isAuthenticated && user) {
      syncEmailVerification();
    }
  }, [isAuthenticated, user]);

  // If authenticated and on login page, redirect to /home (or verify-email if not verified)
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      if (user && !user.verified_email) {
        redirectToMainDomain('/verify-email');
      } else {
        redirectToMainDomain('/home');
      }
    }
  }, [isAuthenticated, location, user]);

  // If authenticated and on root path, redirect to /home (or verify-email if not verified)
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/') {
      if (user && !user.verified_email) {
        redirectToMainDomain('/verify-email');
      } else {
        redirectToMainDomain('/home');
      }
    }
  }, [isAuthenticated, location, user]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page if not on login page already
    if (location.pathname !== '/login' && !PUBLIC_ROUTES.includes(location.pathname)) {
      return <Navigate to="/login" replace />;
    }
    // Allow access to public routes
    return <>{children}</>;
  }

  // Check if email is verified for protected routes (except verification page itself)
  if (user && !user.verified_email && location.pathname !== VERIFICATION_ROUTE && !PUBLIC_ROUTES.includes(location.pathname)) {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
